import ApiErrorBanner from '@/components/ApiErrorBanner';
import { useBenchlingLink } from '@/support/benchlingLink/BenchlingLink.utils';
import type { AnimalFiltersEntity } from '@/components/Colony/Animals/Animals.utils';
import Loading from '@/components/Loading';
import APITable from '@/components/UI/Table/Reusable/APITable';
import {
  type UseTableProps,
  type TableFilter,
  type APITableProps,
  type ChangePercentage,
  DEFAULT_CHANGE_PERCENTAGE,
} from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { TanStackColumns, FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _notNil, uuid } from '@/littledash';
import { type Animal, type AnimalListExistenceMeta } from '@/model/Animal.model';
import type { State } from '@/model/State.model';
import * as Auth from '@/support/auth';
import { useFetchContext, useFetchEntity } from '@/support/Hooks/fetch';
import type { UseFetchEntityOutput } from '@/support/Hooks/fetch/useFetchEntity';
import { RouteService } from '@/support/RouteService';
import { type FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { animalBulkActions, animalFilterOptions, animalTableColumns } from './AnimalsTable.utils';
import { type ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList.tsx';
import AnimalsTableNoData from './AnimalsTableNoData';
import { modalAction } from '@/utils/modal.tsx';
import { web as webRoute } from '@/support/route.ts';
import { type AnimalCreateV1 } from '@/model/PlannedAnimal.model.ts';
import { ApiService } from '@/support/ApiService.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import InVivoError from '@/model/InVivoError.ts';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors.tsx';
import { type Study } from '@/model/Study.model.ts';

const searchByOptions = [
  { value: 'name', display: 'Name' },
  { value: 'tail', display: 'Tail' },
  { value: 'ear', display: 'Ear' },
  { value: 'tag', display: 'Tag' },
  { value: 'donor', display: 'Donor' },
  { value: 'animal_number', display: 'Animal No.' },
  { value: 'cage_name', display: 'Cage' },
  { value: 'cage_number', display: 'Cage No.' },
];

interface ContextProps {
  entity: Study;
}

interface AnimalsTableProps {
  enableBulkActions?: boolean;
  onSelectRow?: (selectedFlatRows: Array<{ original: Animal }>) => void;
  onFetchTableData?: () => void;
  queryParams?: Record<string, string>;
  tableMaxHeight?: number;
  hasPlannedAnimals?: boolean;
  defaultFilters?: Array<TableFilter>;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  atunesIntegrationEnabled: features?.atunes_colony_management_integration ?? false,
  // benchling_worklist is a child of benchling_delegated_auth; the server's feature resolution already
  // excludes children of a disabled parent, so this single flag reflects both.
  benchlingWorklistEnabled: features?.benchling_worklist ?? false,
}));

const inReviewSelector = (state: State) => state.ui.settings.studyState.pageStates.animals;

const AnimalsTable: FC<AnimalsTableProps> = ({
  enableBulkActions = true,
  onSelectRow,
  onFetchTableData,
  queryParams,
  tableMaxHeight,
  hasPlannedAnimals = false,
  defaultFilters = [],
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [columns, setColumns] = useState<TanStackColumns<Animal>>([]);
  const [refetchToken, setRefetchToken] = useState<string>(uuid());
  const [animalsExistInStudy, setAnimalsExistInStudy] = useState<boolean | null>(null);
  const { entity: study } = useFetchContext<ContextProps>();
  const calculations = study?.settings?.calculations;
  const { study_groups: studyGroups, id: studyId } = study;
  const { openModal } = modalAction(useDispatch());
  const { atunesIntegrationEnabled, benchlingWorklistEnabled } = useSelector(featureSelector);
  const benchlingLink = useBenchlingLink();
  const inReview = useSelector(inReviewSelector);

  const { entity: observationOptions }: UseFetchEntityOutput<Array<string>> = useFetchEntity({
    entityType: 'observationFilterOptions',
    params: { id: studyId },
  });

  const { entity: animalFilters, entityError: animalFiltersError }: UseFetchEntityOutput<AnimalFiltersEntity> =
    useFetchEntity({
      entityType: 'animalFilters',
    });

  const [changePercentage, setChangePercentage] = useState<ChangePercentage>(DEFAULT_CHANGE_PERCENTAGE);

  const buildColumns = async () => {
    if (_notNil(animalFilters)) {
      const columns = animalTableColumns(studyId, calculations ?? [], changePercentage, animalFilters.metadata);
      setColumns(columns);
      setLoading(false);
    }
  };

  useEffect(() => {
    buildColumns();
  }, [animalFilters, calculations, changePercentage]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const hasAccess = Auth.isWriteUserForStudy(study);
  const bulkActions: (props: UseTableProps<Animal>) => Array<unknown> = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }) =>
    hasAccess && enableBulkActions
      ? animalBulkActions(
          selectedFlatRows,
          refetchTableData,
          study,
          dispatch,
          navigate,
          atunesIntegrationEnabled,
          inReview,
          benchlingWorklistEnabled,
          benchlingLink.status
        )
      : [];

  useEffect(() => {
    if (
      _notNil(calculations) &&
      _notNil(studyGroups) &&
      _notNil(studyId) &&
      _notNil(observationOptions) &&
      _notNil(animalFilters)
    ) {
      setFilterOptions(animalFilterOptions(calculations, studyGroups, studyId, observationOptions, animalFilters));
    }
  }, [calculations, studyGroups, studyId, observationOptions, animalFilters]);

  const handleCreateNewAnimals = () => {
    navigate(
      webRoute('colony.add.study', {
        studyId: study.id,
      })
    );
  };

  const handleCreateFromPlannedAnimals = () => {
    openModal('CREATE_ANIMALS', {
      studyId: study?.id,
      studyApiId: study?.api_id,
      onSubmit: async (animals: AnimalCreateV1) => {
        try {
          await ApiService.call({
            endpoint: 'POST /api/v1/studies/{studyApiId}/planned/animals/create-animals',
            path: { studyApiId: study?.api_id },
            body: animals,
            options: { onError: { toast: false } },
          });
          successToast('Successfully created animals');
          setRefetchToken(uuid());
        } catch (err) {
          errorToast('Could not create animals');
          ExceptionHandler.captureException(
            new InVivoError('Could not create animals', {
              cause: err,
              slug: 'create-from-planned-animals',
            })
          );
        }
      },
    });
  };

  const handleAnimalsExistInStudy = (additionalProps?: AnimalListExistenceMeta) => {
    setAnimalsExistInStudy(additionalProps?.animals_exist_in_study ?? null);
  };

  let actions: Array<ActionProps> = [];

  if (hasPlannedAnimals) {
    actions = [
      {
        name: 'Create new animals',
        action: () => handleCreateNewAnimals(),
      },
      {
        name: 'Create from planned animals',
        action: () => handleCreateFromPlannedAnimals(),
      },
    ];
  }

  return (
    <div className="pb4" data-test-component="AnimalsTable" data-test-element="animals-table-container">
      {loading ? (
        <div style={{ height: 650 }}>
          <Loading txt="Loading animals..." />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
          {animalFiltersError && (
            <ApiErrorBanner
              title="There was an error fetching table data"
              text="Please try again later. If this keeps occurring please contact support."
              error={animalFiltersError}
              className="mb4"
            />
          )}
          <APITable
            apiInfo={{
              type: 'internalApi',
              route: RouteService.api({
                endpoint: 'GET /api/v1/studies/{studyId}/animals',
                path: { studyId: study?.api_id },
              }).url,
            }}
            queryParams={queryParams}
            includeParam="latestMeasurement,study_group,alerts,cage,metadata,latestObservations"
            columns={columns}
            bulkActions={bulkActions}
            filterOptions={filterOptions}
            defaultFilters={defaultFilters}
            defaultSortBy={{ id: 'number', desc: false }}
            searchPlaceholderText="Search"
            searchQueryByOptions={searchByOptions}
            reduxTableName="animals"
            showViewSelector
            showMetricChangeFrom={true}
            changePercentage={changePercentage}
            onChangePercentageChange={setChangePercentage}
            onSelectRow={onSelectRow as APITableProps<Animal>['onSelectRow']}
            pageSizes={[50, 100, 200, 500, 1000]}
            defaultPageSize={1000}
            onFetchTableData={(_data, additionalProps) => {
              handleAnimalsExistInStudy(additionalProps);
              onFetchTableData?.();
            }}
            testIdPrefix="animals"
            maxHeight={tableMaxHeight}
            refetch={refetchToken}
            customFilterProcessor={createMultiSelectDoubleEncodingProcessor(animalFilters?.metadata ?? [])}
            NoDataComponent={() =>
              inReview ? null : (
                <AnimalsTableNoData
                  atunesIntegrationEnabled={atunesIntegrationEnabled}
                  studyId={study.id}
                  actions={actions}
                  onCreateNewAnimals={handleCreateNewAnimals}
                  animalsExistInStudy={animalsExistInStudy ?? undefined}
                  hasWriteAccess={hasAccess}
                />
              )
            }
          />
        </>
      )}
    </div>
  );
};

export default AnimalsTable;
